import { useEffect, useMemo, useState, type ReactNode } from "react"
import { MessageCircleMore, Save } from "lucide-react"
import { useDispatch } from "react-redux"
import { updateStore } from "../../api/products/post"
import { userData } from "../../store/actions/userAction"
import { buildStorefrontShortUrl } from "../../utils/storefront"
import { isCarStore } from "../../utils/storeCategory"

type StorefrontPublicOrderCardProps = {
  store: any
  defaultContactName?: string
  defaultWhatsappNumber?: string
}

function sanitizeWhatsappNumber(value: any) {
  return String(value || "").replace(/[^\d]/g, "")
}

export default function StorefrontPublicOrderCard({
  store,
  defaultContactName = "",
  defaultWhatsappNumber = "",
}: StorefrontPublicOrderCardProps) {
  const dispatch = useDispatch()
  const [contactName, setContactName] = useState("")
  const [whatsappNumber, setWhatsappNumber] = useState("")
  const [orderInstructions, setOrderInstructions] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  useEffect(() => {
    setContactName(String(store?.publicOrderContactName || defaultContactName || "").trim())
    setWhatsappNumber(String(store?.publicOrderWhatsappNumber || defaultWhatsappNumber || "").trim())
    setOrderInstructions(String(store?.publicOrderInstructions || "").trim())
    setSaving(false)
    setError("")
    setSuccess("")
  }, [
    defaultContactName,
    defaultWhatsappNumber,
    store?.publicOrderContactName,
    store?.publicOrderInstructions,
    store?.publicOrderWhatsappNumber,
    store?._id,
  ])

  const storefrontShortUrl = useMemo(() => buildStorefrontShortUrl(store), [store])
  const normalizedWhatsappNumber = useMemo(
    () => sanitizeWhatsappNumber(whatsappNumber),
    [whatsappNumber]
  )
  const carStore = isCarStore(store)

  const handleSave = async () => {
    if (!store?._id && !store?.id) {
      setError("Boutique introuvable.")
      return
    }

    if (whatsappNumber.trim() && normalizedWhatsappNumber.length < 8) {
      setError("Veuillez saisir un numéro WhatsApp valide.")
      return
    }

    try {
      setSaving(true)
      setError("")
      setSuccess("")

      await updateStore(String(store?._id || store?.id), {
        publicOrderContactName: contactName.trim(),
        publicOrderWhatsappNumber: normalizedWhatsappNumber,
        publicOrderInstructions: orderInstructions.trim(),
      })

      dispatch(userData())
      setSuccess("Contact public mis à jour.")
    } catch (e: any) {
      setError(e?.message || "Impossible d'enregistrer le contact public.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm">
          <MessageCircleMore size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">
            {carStore ? "Commandes WhatsApp du parc auto" : "Commandes WhatsApp de la vitrine"}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            {carStore
              ? "Les visiteurs de votre page concessionnaire seront rediriges vers ce contact WhatsApp au moment de demander un vehicule."
              : "Les visiteurs de votre vitrine publique seront redirigés vers ce contact WhatsApp au moment de commander."}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4">
        <Field label="Nom du contact">
          <input
            type="text"
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
            placeholder="Ex: Boutique Konan"
            className="w-full rounded-xl border border-emerald-100 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
          />
        </Field>

        <Field label="Numéro WhatsApp de commande">
          <input
            type="text"
            inputMode="tel"
            value={whatsappNumber}
            onChange={(event) => setWhatsappNumber(event.target.value)}
            placeholder="2250700000000"
            className="w-full rounded-xl border border-emerald-100 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
          />
          <p className="mt-1 text-xs text-slate-500">
            Utilisez le format international. Exemple: <span className="font-medium">2250700000000</span>
          </p>
        </Field>

        <Field label="Consigne affichée au vendeur">
          <textarea
            value={orderInstructions}
            onChange={(event) => setOrderInstructions(event.target.value)}
            rows={3}
            placeholder="Ex: Envoyez votre nom, votre quartier"
            className="w-full rounded-xl border border-emerald-100 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
          />
        </Field>
      </div>

      {storefrontShortUrl ? (
        <div className="mt-4 rounded-xl border border-white/70 bg-white/80 px-3 py-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">
            {carStore ? "Parc auto concerne" : "Vitrine concernée"}
          </p>
          <p className="mt-1 break-all text-sm font-medium text-slate-700">{storefrontShortUrl}</p>
          {normalizedWhatsappNumber ? (
            <p className="mt-2 text-xs text-slate-500">
              Redirection prévue vers <span className="font-medium">wa.me/{normalizedWhatsappNumber}</span>
            </p>
          ) : (
            <p className="mt-2 text-xs text-amber-700">
              {carStore
                ? "Ajoutez un numero WhatsApp pour activer les demandes directes depuis le parc auto."
                : "Ajoutez un numéro WhatsApp pour activer la commande directe depuis la vitrine."}
            </p>
          )}
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-emerald-700">
          {success}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Save size={16} />
          {saving ? "Enregistrement..." : "Enregistrer le contact public"}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  )
}
